/**
 * BarcodeInput Component
 * Handles barcode input and search functionality
 */

import { useRef } from 'react';
import type { InventoryItem } from '@/types/index.ts';

interface BarcodeInputProps {
  barcode: string;
  onBarcodeChange: (value: string) => void;
  onSearch: () => void;
  isSearching: boolean;
  selectedItem: InventoryItem | null;
  setShowScanner: (show: boolean) => void;
}

export default function BarcodeInput({
  barcode,
  onBarcodeChange,
  onSearch,
  isSearching,
  setShowScanner,
}: BarcodeInputProps) {
  const barcodeInputRef = useRef<HTMLInputElement>(null);

  const handleBarcodeChange = (value: string) => {
    
    const numericValue = value.replace(/\D/g, '');
    onBarcodeChange(numericValue);
  };

  return (
    <div className="flex gap-2">
      <div className="relative flex-1">
        <i className="ri-barcode-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg"></i>
        <input
          ref={barcodeInputRef}
          type="text"
          value={barcode}
          onChange={(e) => handleBarcodeChange(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && onSearch()}
          placeholder="Click here and scan barcode..."
          disabled={isSearching}
          className="w-full pl-10 pr-12 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#50b3e1] focus:border-[#50b3e1] disabled:bg-gray-50 disabled:cursor-not-allowed"
        />
        <button
          type="button"
          onClick={() => setShowScanner(true)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#50b3e1] transition-colors cursor-pointer"
          title="Scan barcode with camera"
        >
          <i className="ri-camera-line text-lg"></i>
        </button>
      </div>
      <button
        onClick={onSearch}
        disabled={isSearching || !barcode.trim()}
        className="px-4 py-2 bg-[#50b3e1] text-white rounded-lg text-sm font-medium hover:bg-[#0997a4] transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isSearching ? (
          <span className="flex items-center gap-2">
            <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
            Searching...
          </span>
        ) : (
          'Search'
        )}
      </button>
    </div>
  );
}
